import { OrganizationConfig, OrganizationRevenue } from "../models/revenue/BillingConfig";

export interface RevenueOrganizationFilter {
    organizations: OrganizationConfig[];
    names: Record<string, string>;
    revenueByOrg: OrganizationRevenue[];
    applicationsByOrg: Record<string, { id?: string; name?: string }[]>;
    orgLoadErrors: Record<string, string>;
    search: string;
    // Only hide organizations once the month's data has fully loaded.
    hideEmpty: boolean;
}

export const matchesRevenueSearch = (text: string | undefined, search: string): boolean =>
    !search.trim() || (text || "").toLowerCase().includes(search.trim().toLowerCase());

export const getRevenueOrganizationName = (org: OrganizationConfig, names: Record<string, string>): string =>
    names[org.organizationId] || org.organizationName || org.organizationId;

// Organizations whose own name matches the search show all their applications;
// otherwise only applications whose names match are shown.
export const organizationNameMatches = (
    org: OrganizationConfig,
    names: Record<string, string>,
    search: string
): boolean => matchesRevenueSearch(getRevenueOrganizationName(org, names), search);

// An organization has no billable applications this month when it loaded successfully, nothing
// was billable (e.g. no live apps) and none of its apps were excluded by hand, so it can be hidden.
const hasNoBillableApplications = (
    org: OrganizationConfig,
    revenueByOrg: OrganizationRevenue[],
    orgLoadErrors: Record<string, string>
): boolean => {
    const revenue = revenueByOrg.find(item => item.organizationId === org.organizationId);
    return !!revenue && revenue.applications.length === 0 && !orgLoadErrors[org.organizationId]
        && org.ignoredApplicationIds.length === 0;
};

export const filterRevenueOrganizations = (
    filter: RevenueOrganizationFilter
): { visible: OrganizationConfig[]; hiddenEmptyCount: number } => {
    const { organizations, names, revenueByOrg, applicationsByOrg, orgLoadErrors, search, hideEmpty } = filter;
    const empty = hideEmpty
        ? organizations.filter(org => hasNoBillableApplications(org, revenueByOrg, orgLoadErrors))
        : [];
    const visible = organizations.filter(org => {
        if (empty.includes(org)) return false;
        if (organizationNameMatches(org, names, search)) return true;
        const revenueApps = revenueByOrg.find(item => item.organizationId === org.organizationId)?.applications || [];
        return revenueApps.some(app => matchesRevenueSearch(app.applicationName || app.applicationId, search))
            || (applicationsByOrg[org.organizationId] || []).some(app => matchesRevenueSearch(app.name, search));
    });
    return { visible, hiddenEmptyCount: empty.length };
};
